import axios from 'axios';
import { API_URL } from '../constants';
import { toast } from 'react-toastify';
import { useTokens } from '../context/TokenContext';

export const usePageAPI = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const authHeader = {
    headers: {
      'Authorization': `Bearer ${adminAuthToken}`,
      'Content-Type': 'application/json'  // Changed content type to 'application/json' since it is more common for APIs
    }
  };

  const handleError = (error, context) => {
    console.error(`Error in ${context}:`, error);
    return { data: [], dataCount: 0 };  // Default return for errors
  };

  const handleSuccess = (res) => {
    return res.data || {};  // Ensure we always return a valid response object
  };

  const fetchData = async (apiRoute, queryParams = {}, loadingState) => {
    loadingState(true);
    const loadingToast = toast.loading('Loading...');
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => {
        if (key === 'searchBy' || key === 'searchValue') {
          // Only include searchBy and searchValue if both are not empty
          return value !== null && value !== undefined && value !== '';
        }
        // Include all other parameters that are not null, undefined, or empty
        return value !== null && value !== undefined && value !== '';
      })
    );
    
    // Optional: Check if both searchBy and searchValue are present, and if one of them is empty, remove both
    if (!filteredParams.searchBy || !filteredParams.searchValue) {
      delete filteredParams.searchBy;
      delete filteredParams.searchValue;
    }
    try {
      const response = await axios.get(`${API_URL}/${apiRoute}`, {
        params: queryParams,
      });
      toast.update(loadingToast, { render: 'Data fetched successfully!', type: 'success', isLoading: false, autoClose: 5000, });
      return response.data;
    } catch (error) {
      toast.update(loadingToast, { render: 'Error fetching data!', type: 'error', isLoading: false, autoClose: 5000, });
      console.error('API error:', error);
      return null;
    } finally {
      loadingState(false);
    }
  };

  const fetchPages = async ({ type = 'pages', currentPage, limit, searchBy, searchValue, termType, status, parent, setLoading, setProgress, }) => {
    setLoading(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 80) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 200);

    const isValid = (value) => value !== null && value !== undefined && value !== '';

    try {
      let url = `${API_URL}/${type}?page=${currentPage}&limit=${limit}`;

      if (isValid(searchBy)) url += `&searchBy=${searchBy}`;
      if (isValid(searchValue)) url += `&searchValue=${searchValue}`;
      if (isValid(status)) url += `&status=${status}`;
      if (isValid(parent)) url += `&parent=${parent}`;
      if (isValid(termType)) url += `&termType=${termType}`;

      const res = await axios.get(url, authHeader);
      const { data = [], dataCount = 0 } = res.data || {};

      setProgress(100);
      return { data, dataCount };
    } catch (error) {
      return handleError(error, 'fetchPages');
    } finally {
      clearInterval(progressInterval);
      setLoading(false);
    }
  };

  const fetchPage = async ({ type, pageId, setLoading, setProgress }) => {
    setLoading(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 80) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 200);

    try {
      const res = await axios.get(`${API_URL}/${type}/${pageId}`, authHeader);
      setProgress(100);
      return res;
    } catch (error) {
      return handleError(error, 'fetchPage');
    } finally {
      setLoading(false);
    }
  };

  const createPage = async ({ type, formDataToSubmit }) => {
    try {
      const res = await axios.post(`${API_URL}/${type}`, formDataToSubmit, authHeader);
      return res;
    } catch (error) {
      return handleError(error, 'createPage');
    }
  };

  const updatePage = async ({ type, pageId, formDataToSubmit }) => {
    try {
      const res = await axios.put(`${API_URL}/${type}/${pageId}`, formDataToSubmit, authHeader);
      return res;
    } catch (error) {
      return handleError(error, 'updatePage');
    }
  };

  const deletePage = async ({ type, id, setLoading }) => {
    setLoading(true);
    try {
      const action = await axios.delete(`${API_URL}/${type}/${id}`, authHeader);
      return action.data.message || 'Page deleted successfully!';
    } catch (error) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : handleError(error, 'deletePage');
    } finally {
      setLoading(false);
    }
  };

  const actionBulk = async ({ type, ids, action, setLoading }) => {
    setLoading(true);

    try {
      const data = {
        action,
        ids,
      };

      const res = await axios.post(`${API_URL}/${type}/bulk`, data, authHeader);

      if (res.data.message) {
        return `${res.data.message}`;
      }

      return 'Action applied successfully!';
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return handleError(error, 'bulkAction');
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchData,
    fetchPages,
    fetchPage,
    createPage,
    updatePage,
    deletePage,
    actionBulk,
  };
};
